const CATALOG_TOPIC = "wwt-podcast-catalog";

export function blankCatalog() {
  return { featuredIds: [], albums: [] };
}

function parseCatalog(post) {
  let data = {};
  try {
    data = JSON.parse(post?.body || "{}");
  } catch {
    data = {};
  }
  const albums = (Array.isArray(data.albums) ? data.albums : [])
    .filter((album) => album && album._id && String(album.title || "").trim())
    .map((album) => ({
      _id: String(album._id),
      title: String(album.title).trim(),
      coverUrl: album.coverUrl || "",
      trackIds: (Array.isArray(album.trackIds) ? album.trackIds : []).map(String),
    }));
  return {
    featuredIds: (Array.isArray(data.featuredIds) ? data.featuredIds : []).map(String),
    albums,
  };
}

export function decorateEpisode(post, catalog) {
  const member = catalog.albums.find((album) => album.trackIds.includes(String(post._id)));
  const albumId = member?._id || (post.albumId ? String(post.albumId) : "");
  const isFeatured =
    catalog.featuredIds.includes(String(post._id)) ||
    post.isFeatured === true ||
    post.isFeatured === "true";
  return { ...post, albumId, isFeatured };
}

export function normalizeTrack(post, albumsById) {
  const album = post.albumId ? albumsById.get(String(post.albumId)) : null;
  return {
    ...post,
    _id: String(post._id),
    title: post.topic || post.title || "Untitled episode",
    description: post.description || "",
    lyrics: post.lyrics || post.body || "",
    posterUrl: post.thumbnailUrl || post.posterUrl || null,
    albumId: album ? String(album._id) : "",
    albumTitle: album?.title || "",
    albumCoverUrl: album?.coverUrl || null,
    mood: post.subCategory || "Markets",
    categories: [post.subCategory || "Markets"],
    language: post.language || "",
    tags: Array.isArray(post.tags) ? post.tags : [],
    duration: Number(post.duration) || 0,
    listens: Number(post.viewer) || 0,
    likes: Array.isArray(post.like) ? post.like.length : 0,
    isFeatured: post.isFeatured === true || post.isFeatured === "true",
  };
}

export function playbackUrl(track) {
  return track?.audioUrl || track?.videoUrl || "";
}

export function isTestEpisode(track) {
  return /\btest(?:ing)?\b/i.test(track?.title || track?.topic || "");
}

export function isVideoTrack(track) {
  return !track?.audioUrl && !!track?.videoUrl;
}

async function readJson(url) {
  let lastError = new Error("Could not load podcast");
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const separator = url.includes("?") ? "&" : "?";
      const response = await fetch(`${url}${separator}_=${Date.now()}`, { cache: "no-store" });
      if (!response.ok) throw new Error("Could not load podcast");
      return await response.json();
    } catch (error) {
      lastError = error instanceof Error ? error : lastError;
      if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, 400));
    }
  }
  throw lastError;
}

function rowsOf(payload) {
  return Array.isArray(payload) ? payload : payload?.posts || [];
}

export async function saveCatalogAlbum(album, user) {
  const payload = await readJson("/api/posts?category=AudioCatalog&page=1");
  const posts = rowsOf(payload);
  const catalogPost = posts.find((post) => post.topic === CATALOG_TOPIC) || posts[0];
  const catalog = parseCatalog(catalogPost);
  const albumId = String(album._id);
  const nextAlbum = {
    _id: albumId,
    title: String(album.title || "").trim(),
    coverUrl: album.coverUrl || "",
    trackIds: (catalog.albums.find((item) => item._id === albumId)?.trackIds || []).map(String),
  };
  const nextAlbums = [nextAlbum, ...catalog.albums.filter((item) => item._id !== albumId)];
  const body = JSON.stringify({ featuredIds: catalog.featuredIds, albums: nextAlbums });
  const editing = Boolean(catalogPost?._id);
  const response = await fetch(editing ? `/api/posts/${catalogPost._id}` : "/api/posts", {
    method: editing ? "PUT" : "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.token}` },
    body: JSON.stringify({
      username: user.user.username,
      userId: user.user._id,
      role: String(user.user.role),
      topic: CATALOG_TOPIC,
      category: "AudioCatalog",
      body,
      ...(editing ? { isAdmin: true, userRole: Number(user.user.role) } : {}),
    }),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.message || result.error || "Album could not be synced to mobile.");
  return nextAlbum;
}

// Audio is returned in one page. Podcast is paged at 5, same as the mobile screen.
async function loadCategoryPosts(category) {
  const first = await readJson(`/api/posts?category=${encodeURIComponent(category)}&page=1`);
  const rows = rowsOf(first);
  const totalPages = Math.min(40, Number(first?.totalPages) || 1);
  if (totalPages > 1) {
    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) =>
        readJson(`/api/posts?category=${encodeURIComponent(category)}&page=${index + 2}`)
      )
    );
    rest.forEach((payload) => rows.push(...rowsOf(payload)));
  }
  return rows;
}

export async function loadPodcastData() {
  const [audioResult, podcastResult, catalogResult, albumsResult] = await Promise.allSettled([
    loadCategoryPosts("Audio"),
    loadCategoryPosts("Podcast"),
    readJson("/api/posts?category=AudioCatalog&page=1"),
    readJson("/api/albums"),
  ]);
  if (audioResult.status === "rejected" && podcastResult.status === "rejected") {
    const reason = podcastResult.reason;
    throw reason instanceof Error ? reason : new Error("Could not load podcast");
  }
  const audioRows = audioResult.status === "fulfilled" ? audioResult.value : [];
  const podcastRows = podcastResult.status === "fulfilled" ? podcastResult.value : [];
  const catalogPayload = catalogResult.status === "fulfilled" ? catalogResult.value : { posts: [] };
  const catalogPosts = rowsOf(catalogPayload);
  const catalog = parseCatalog(
    catalogPosts.find((post) => post.topic === CATALOG_TOPIC) || catalogPosts[0]
  );
  const apiAlbums = albumsResult.status === "fulfilled" && Array.isArray(albumsResult.value)
    ? albumsResult.value.filter((album) => album?._id && String(album.title || "").trim()).map((album) => ({
      ...album,
      _id: String(album._id),
      title: String(album.title).trim(),
      coverUrl: album.coverUrl || "",
    }))
    : [];
  const catalogIds = new Set(catalog.albums.map((album) => String(album._id)));
  const albums = [
    ...catalog.albums,
    ...apiAlbums.filter((album) => !catalogIds.has(String(album._id))),
  ];
  const albumsById = new Map(albums.map((album) => [album._id, album]));
  const seen = new Set();
  const allTracks = [...podcastRows, ...audioRows]
    .filter((post) => {
      if (!post?._id || seen.has(String(post._id))) return false;
      if (!(post.audioUrl || post.videoUrl || post.youtubeUrl)) return false;
      seen.add(String(post._id));
      return true;
    })
    .map((post) => normalizeTrack(decorateEpisode(post, catalog), albumsById));
  return {
    tracks: allTracks.filter((track) => !isTestEpisode(track)),
    testTracks: allTracks.filter(isTestEpisode),
    albums,
  };
}

export function albumSummaries(tracks, albums) {
  const counts = new Map();
  tracks.forEach((track) => {
    if (!track.albumId) return;
    counts.set(track.albumId, (counts.get(track.albumId) || 0) + 1);
  });
  return albums.map((album) => ({
    ...album,
    count: counts.get(album._id) || 0,
  }));
}

export function queryTracks(tracks, params = {}) {
  const q = (params.search || "").trim().toLowerCase();
  let rows = tracks.filter((track) => {
    if (params.albumId && track.albumId !== params.albumId) return false;
    if (params.mood && track.mood !== params.mood) return false;
    if (params.featured && !track.isFeatured) return false;
    if (params.hasVideo && !(track.videoUrl || track.youtubeUrl)) return false;
    if (!q) return true;
    return [track.title, track.description, track.mood, track.albumTitle, track.username, ...track.tags]
      .join(" ")
      .toLowerCase()
      .includes(q);
  });
  const timestamp = (track) => Date.parse(track.createdAt || "") || 0;
  rows.sort((a, b) => {
    switch (params.sortBy) {
      case "oldest":
        return timestamp(a) - timestamp(b);
      case "popular":
        return b.listens - a.listens;
      case "liked":
        return b.likes - a.likes;
      case "rating":
        return (b.avgRating || 0) - (a.avgRating || 0);
      case "az":
        return a.title.localeCompare(b.title);
      default:
        return timestamp(b) - timestamp(a);
    }
  });
  return rows;
}

export function formatClock(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  const remain = total % 60;
  return `${minutes}:${String(remain).padStart(2, "0")}`;
}

export function plainText(value) {
  if (!value) return "";
  return String(value)
    .replace(/<br\s*\/?\s*>/gi, "\n")
    .replace(/<\/(?:p|div|li|h[1-6]|blockquote|pre)\s*>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\r\n?/g, "\n")
    .replace(/[\t ]+\n/g, "\n")
    .replace(/\n[\t ]+/g, "\n")
    .replace(/[\t ]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
